<template>
  <div>
    <modal :show-close="modalConfig.showClose"
          :wrapper-classes="modalConfig.wrapperClasses"
          @close="closeModal()">
      <h2 slot="header" class="modal-title mt-5 text-2xl text-center sm:text-4xl sm:mt-10">
        any link
      </h2>
      <h4 slot="subHeader" class="text-xs text-gray-700 text-center font-light px-0 sm:px-10 sm:text-xl">
        add multiple URLs to your stashboard so your customers now have a single point of access for all your important links
      </h4>
      <div slot="body">
        <sb-loading-icon v-if="loading"  class="w-20 sm:w-72 m-auto" />
        <template v-else>
          <div class="hidden sm:grid modal-content-cols">
            <div></div>
            <div class="text-left self-end">title<sup>*</sup></div>
            <div class="text-left self-end">url<sup>*</sup></div>
            <div class="text-center">thumbnail image</div>
            <div></div>
          </div>
          <div class="px-0 py-2">
            <div class="grid modal-content-cols form">
              <template v-for="(link, index) in anyLinksDraftState">
                <div class="item text-center self-center">
                  <fai :icon="['far', 'chevron-up']"
                      class="chevron"
                      :class="{ disabled: index === 0 }"
                      @click="moveLinkUp(index)" />
                  <span class="position">{{ index + 1 }}</span>
                  <fai :icon="['far', 'chevron-down']"
                      class="chevron"
                      :class="{ disabled: index === (anyLinksDraftState.length - 1)}"
                      @click="moveLinkDown(index)" />
                </div>
                <div class="hidden sm:block item relative self-center">
                  <input type="text" :class="{error: !link.title}" v-model="link.title" placeholder="add title" maxlength="25" class="item-input" :data-cy="`any-link-title-${index}-input`" />
                  <span class="notice">max 25 characters</span>
                </div>
                <div class="hidden sm:block item relative self-center">
                  <input type="text" :class="{error: isInvalidUrl(link.url)}" v-model="link.url" placeholder="add url" class="item-input" :data-cy="`any-link-url-${index}-input`" />
                  <span class="notice">include http:// or https://</span>
                </div>
                <div class="block sm:hidden flex flex-col justify-center">
                  <input type="text" :class="{error: !link.title}" v-model="link.title" placeholder="add title" maxlength="25" class="item-input mt-0 mb-0" :data-cy="`any-link-title-${index}-input`" />
                  <input type="text" :class="{error: isInvalidUrl(link.url)}" v-model="link.url" placeholder="add url" class="item-input mt-2 mb-0" :data-cy="`any-link-url-${index}-input`" />
                </div>
                <div class="item text-center self-center">
                  <div class="block sm:hidden text-center text-xs">thumbnail image</div>
                  <div v-if="link.thumbnail">
                    <div class="thumbnail-image" :style="{ backgroundImage: `url(${cleanThumbnailUrl(link.thumbnail.url)})` }"></div>
                    <a class="text-xs mt-1 px-2.5 py-1.5" @click="link.thumbnail = null" :data-cy="`any-link-thumbnail-image-${index}-remove-button`">remove</a>
                  </div>
                  <div v-else class="thumbnail-image">
                    <a v-file-upload:png,gif,jpeg="($event) => processThumbnail($event, index)" class="button" :data-cy="`any-link-thumbnail-image-${index}-upload-button`">
                      <fai :icon="['fas', 'cloud-upload-alt']" />
                    </a>
                  </div>
                </div>
                <div class="item text-center self-center">
                  <span class="delete-btn" @click="anyLinksDraftState.splice(index, 1)" :data-cy="`any-link-delete-${index}-button`">&times;</span>
                </div>
              </template>
            </div>
          </div>
          <div class="text-right pt-2 pr-8 sm:pt-6 sm:pr-24">
            <a @click="addRowToAnyLinkDraftState()" :class="{ disabled : !isAnyLinksDraftStateLessThanTenRows }" data-cy="any-link-add-new-link-button">
              <template v-if="isAnyLinksDraftStateLessThanTenRows">add new link</template>
              <template v-else>max limit reached</template>
            </a>
          </div>
          <div class="previous-any-link-list mt-6">
            <h5>previously used URLs</h5>
            <div class="grid modal-previous-cols w-full pl-7">
              <div class="text-left self-end text-xs sm:text-base">title</div>
              <div class="hidden sm:block text-left self-end text-xs sm:text-base">url</div>
              <div class="text-center text-xs sm:text-base">thumbnail image</div>
              <div></div>
              <div></div>
            </div>
            <div class="any-link-list max-h-48 overflow-y-auto py-2 px-0">
              <div class="any-link-list__row grid modal-previous-cols relative pl-7 w-full" v-for="(link, index) in previouslyUsedAnyLinks">
                <div class="absolute top-1 sm:top-2 left-0 text-3xl cursor-pointer hover:text-red-600" @click="openConfirmModal(link.id)">&times;</div>
                <div class="item self-center">{{ link.title }}</div>
                <div class="hidden sm:block item self-center text-ellipsis overflow-hidden w-40">{{ link.url | stripHttpProtocolUtil }}</div>
                <div class="item text-center self-center">
                  <div class="thumbnail-image" :style="{ backgroundImage: `url(${link.thumbnail ? cleanThumbnailUrl(link.thumbnail.url) : defaultThumbnailImage})` }"></div>
                </div>
                <div class="item text-center self-center">
                  <a class="text-xs sm:text-base" :class="{ disabled : isAllAnyLinksRowsFilled }" @click="addRowToAnyLinkDraftState(link)" :data-cy="`any-link-add-link-${index}-button`">add link</a>
                </div>
                <div v-if="index != (previouslyUsedAnyLinks.length - 1)" class="separator"></div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div slot="action" class="modal-action-click">
        <button class="button done-button" :disabled="isDoneButtonDisabled" @click="clickedDone()" data-cy="any-link-done-button">done</button>
        <span class="error">{{ error }}</span>
      </div>
    </modal>
    <div class="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-20 z-10" v-if="showWarningModal">
      <div class="confirm-delete font-primary border border-solid border-blue bg-white pt-10 px-0 pb-3 rounded-2xl text-center w-full sm:max-w-lg relative">
        <div class="text-4xl absolute top-0 left-3 cursor-pointer" @click="closeConfirmModal()">&times;</div>
        <h3 class="text-gray-800 px-7">
          are you sure you would <br />
          like to remove <br />
          the previous used url?
        </h3>

        <div class="sb-row center bottom-spacing" slot="action">
          <button
            class="button close"
            @click="closeConfirmModal()"
          >
            cancel
          </button>
          <button
            class="button proceed"
            @click="deletePrviouslyUsedLink()"
          >
            confirm
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from "../../../shared/Modal.vue";
import {campaignAPI} from '../../../../services/api/campaignAPI.js';
import {autoCampaignMessageAPI} from '../../../../services/api/autoCampaignMessageAPI.js';
import {anylinkAPI} from '../../../../services/api/anylinkAPI.js';
import defaultThumbnailImage from 'stashboard/default_thumbnail_image.png';
import {stripHttpProtocolUtil, arrayMoveUtil, isUrlFormatValidUtil} from "../../../../utilities";
import {mapGetters} from "vuex";
import _ from 'lodash';

const genericErrorMessage = "error, please refresh or contact support";

export default {
  props: {
    anylinks: Array,
    communicationId: Number
  },

  name: "AnyLinkModal",

  components: {Modal},

  data(){
    return {
      anyLinksDraftState: [],
      defaultThumbnailImage,
      error: null,
      loading: false,
      showWarningModal: false,
      linkToDelete: null,
      modalConfig: {
        showClose: true,
        wrapperClasses: ["extensions-modal","anylink-modal"]
      },
      previouslyUsedAnyLinks: []
    }
  },

  async created(){
    this.loading = true
    await this.getPreviouslyUsedAnyLinks();
    this.anyLinksDraftState = _.cloneDeep(this.anylinks)
    if(this.anylinks.length === 0) {
      this.addRowToAnyLinkDraftState()
    }
    this.loading = false
  },

  computed: {
    ...mapGetters(['campaign']),

    communicationAPI(){
      return this.campaign ? campaignAPI : autoCampaignMessageAPI
    },

    currentMerchant() {
      return this.$store.state.currentMerchant
    },

    isAllAnyLinksRowsFilled() {
       return this.anyLinksDraftState.length === 10 && this.anyLinksDraftState.every(link => link.title !== "");
    },

    isAnyLinksDraftStateLessThanTenRows() {
      return this.anyLinksDraftState.length < 10
    },

    isDoneButtonDisabled() {
      return this.loading || this.anyLinksDraftState.some(link => !link.title || this.isInvalidUrl(link.url))
    }
  },

  methods: {
    addRowToAnyLinkDraftState(newLink) {
      if(!newLink) {
        newLink = {
          priority: null,
          thumbnail: null,
          title: "",
          url: ""
        }
        this.anyLinksDraftState.push({...newLink});
        return;
      } else {
        const nextAvailableSpotIndex = this.anyLinksDraftState.findIndex(link => link.title === "");
        if(nextAvailableSpotIndex === -1 && this.anyLinksDraftState.length > 0){
          this.anyLinksDraftState.push({...newLink});
          return;
        } else {
          this.anyLinksDraftState.splice(nextAvailableSpotIndex, 1, {...newLink});
          return;
        }
      }
    },

    cleanThumbnailUrl(url) {
      if(url.startsWith("http")) {
        return `${url}?h=47&w=47`
      }
      return url
    },

    async clickedDone() {
      this.loading = true
      const anyLinksWithPriorities = this.anyLinksDraftState.map((anyLink, index) => ({ ...anyLink, priority: index + 1 }))
      try {
        const resp = await this.communicationAPI.putUpdateAnyLinks(this.communicationId, anyLinksWithPriorities)
        this.loading = false
        this.$emit("update:anylinks", anyLinksWithPriorities)
        this.$emit("hideModal", false)
      }catch(e){
        this.error = genericErrorMessage
      }
    },

    closeModal() {
      this.anyLinksDraftState = []
      this.$emit('hideModal', false)
    },


    isInvalidUrl(url) {
      return !url || !isUrlFormatValidUtil(url)
    },

    async getPreviouslyUsedAnyLinks() {
      const previouslyUsedAnyLinks = await anylinkAPI.getPreviouslyUsedAnyLinks()

      if(previouslyUsedAnyLinks) {
        this.previouslyUsedAnyLinks = previouslyUsedAnyLinks
      }else{
        this.error = genericErrorMessage
      }
    },

    async deletePrviouslyUsedLink() {
      try {
        this.showWarningModal = false;
        await anylinkAPI.removePreviouslyUsedAnyLink(
          this.linkToDelete,
        );
        this.previouslyUsedAnyLinks = this.previouslyUsedAnyLinks.filter(link => {
          return link.id !== this.linkToDelete;
        });
      }
      catch(err) {
        console.log(err);
      }
    },

    moveLinkUp(index) {
      this.anyLinksDraftState = arrayMoveUtil(this.anyLinksDraftState, index, index - 1)
    },

    moveLinkDown(index) {
      this.anyLinksDraftState = arrayMoveUtil(this.anyLinksDraftState, index, index + 1)
    },

    processThumbnail(file, index){
      const reader = new FileReader()
      reader.onload = e => {
        file.url = e.target.result
        this.anyLinksDraftState[index].thumbnail = file
      }
      reader.readAsDataURL(file)
    },
    openConfirmModal(linkId) {
      if (linkId) {
        this.showWarningModal = true;
        this.linkToDelete = linkId;
      }
    },
    closeConfirmModal() {
      this.showWarningModal = false;
      this.linkToDelete = null;
    }
  },

  filters: {
    stripHttpProtocolUtil
  }
}
</script>

<style scoped>
.button {
  font-size: 0.8rem;
  font-family: 'AileronThin';
}
.button.close {
  background-color: gray;
  margin-right: 30px;
  margin-left: 20px;
  font-size: 1.2rem;
}
.button.proceed {
  margin-right: 20px;
  font-size: 1.2rem;
}

.anylink-modal span {
  font-size: .8rem;
  line-height: 1.25;
}

.anylink-modal .error {
  font-size: 1em;
}

.anylink-modal .modal-content-cols {
  gap: 0px 35px;
  grid-template-columns: 1fr 4fr 5fr 1.5fr 1fr;
}

.anylink-modal .previous-any-link-list .modal-previous-cols {
  gap: 0px 35px;
  grid-template-columns: 4fr 5fr 2fr 2fr;
}

.anylink-modal .previous-any-link-list .grid {
  grid-auto-rows: auto;
}


.anylink-modal .grid.form {
  gap: 20px 35px;
}


.anylink-modal .previous-any-link-list .item {
  font-family: "AileronThin";
}

.anylink-modal a {
  font-family: "AileronLight";
}

.anylink-modal h5 {
  font-family: "AileronLight";
}

.anylink-modal .form input.error {
  border-color: #f91253;
}

.anylink-modal .thumbnail-image svg {
  font-size: 1.5em;
}

.anylink-modal .notice {
  color: gray;
  left: 3px;
  position: absolute;
  top: 40px;
}

.anylink-modal .chevron {
  color: #128ff9;
  cursor: pointer;
  display: block;
  margin: 0 auto;
}

.anylink-modal .disabled {
  opacity: 0.5;
  pointer-events: none;
}

.anylink-modal .delete-btn {
  cursor: pointer;
  font-size: 2em;
}

.anylink-modal .delete-btn:hover {
  color: #f91253;
}

.anylink-modal .modal-action-click {
  text-align: center;
}

.anylink-modal .thumbnail-image {
  background-color: #fff;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 50%;
  height: 47px;
  margin: 0 auto;
  width: 47px;
}

.anylink-modal .thumbnail-image img {
  height: auto;
  width: 100%;
}

.anylink-modal .done-button {
  font-family: "AileronThin";
  font-size: 1.5em;
  padding: 0.85em 2em;
  min-width: 10rem;
  top: 1rem !important;
}

.anylink-modal .grid .separator {
  background-color: rgba(18, 143, 249, 0.2);
  grid-column: 1 / -1;
  height: 1px;
  margin: 8px 0;
}

@media only screen and (max-width: 600px) {
  .anylink-modal .modal-content-cols {
    gap: 0px 10px;
    grid-template-columns: 0.5fr 12fr 1fr 0.5fr;
  }
  .anylink-modal .grid.form {
    gap: 10px 10px;
  }
  .anylink-modal .thumbnail-image a.button {
    margin-top: 10px;
    padding: 9px 10px;
  }
  .anylink-modal .thumbnail-image {
    height: 40px;
    width: 40px;
  }
  .anylink-modal .thumbnail-image svg {
    font-size: 1em;
  }
  .anylink-modal .previous-any-link-list .modal-previous-cols {
    gap: 0px 10px;
    grid-template-columns: 5fr 2fr 2fr;
  }
}
</style>
